import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { FaUpload, FaTrash, FaImage, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  DELIVERY_AFTER_PREVIEW_VALUE,
  DELIVERY_OPTIONS,
  getDeliveryOption,
  isValidDeliveryPreference,
  normalizeDeliveryPreference
} from '../config/pricing';
import {
  CHILD_AGE_OPTIONS as childAgeOptions,
  CHILD_PHOTO_LIMIT,
  COMPRESSED_IMAGE_MAX_SIZE_MB,
  CUSTOM_NEED_VALUE,
  CUSTOM_STORY_WORLD_VALUE,
  DEFAULT_STORY_WORLD_VALUE,
  EDUCATIONAL_FOCI as educationalFoci,
  GENDER_OPTIONS as genderOptions,
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_EXTENSION_BY_MIME,
  INSPIRATION_IMAGE_LIMIT,
  isAllowedStorybookImageFile,
  MAIN_CHARACTER_OPTIONS as mainCharacterOptions,
  MAX_IMAGE_UPLOAD_SIZE_BYTES,
  MAX_TOTAL_IMAGE_UPLOAD_SIZE_BYTES,
  STORY_LENGTH_OPTIONS as storyLengthOptions,
  STORY_WORLDS as storyWorlds,
  STORYBOOK_MAX_FIELD_LENGTHS as MAX_FIELD_LENGTHS,
  SUPPORT_NEEDS as supportNeeds
} from '../config/storybookOptions';
import '../styles/storybook-form.css';

const STEP_DETAILS = [
  { number: 1, key: 'child' },
  { number: 2, key: 'story' },
  { number: 3, key: 'visuals' },
  { number: 4, key: 'review' }
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STORYBOOK_DRAFT_KEY = 'massal_storybook_request_draft_v1';
const LEGACY_STORYBOOK_DRAFT_KEYS = ['massal_storybook_order_draft_v1'];
const STORYBOOK_DRAFT_KEYS = [STORYBOOK_DRAFT_KEY, ...LEGACY_STORYBOOK_DRAFT_KEYS];
const STORYBOOK_DRAFT_TTL_MS = 30 * 60 * 1000;
const STORYBOOK_IMAGE_FIELDS = new Set(['childPhotos', 'inspirationImages']);
const STORYBOOK_BOOLEAN_FIELDS = new Set(['guardianConsent']);
const STORYBOOK_FINAL_CONFIRMATION_FIELDS = new Set(['guardianConsent', 'reviewConfirmed']);
const STORYBOOK_DRAFT_USER_ID_MAX_LENGTH = 120;

const asArray = (value) => (Array.isArray(value) ? value : []);

const hasOptionValue = (options, value) => (
  options.some((option) => String(option.value) === String(value))
);

const hasPrimitiveValue = (values, value) => (
  values.some((optionValue) => String(optionValue) === String(value))
);

const normalizeOptionValue = (options, value, fallback = '') => {
  const stringValue = normalizeFormStringValue('', value);
  return stringValue && hasOptionValue(options, stringValue) ? stringValue : fallback;
};

const normalizePrimitiveOptionValue = (values, value, fallback = '') => {
  const stringValue = normalizeFormStringValue('', value);
  return stringValue && hasPrimitiveValue(values, stringValue) ? stringValue : fallback;
};

const hasAcceptedClientConsent = (value) => (
  value === true
  || ['true', '1', 'on', 'yes'].includes(String(value).trim().toLowerCase())
);

const normalizeFormStringValue = (field, value, fallback = '') => {
  if (value === undefined || value === null) {
    return fallback;
  }

  const stringValue = typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
  const normalizedValue = field === 'parentEmail' ? stringValue.toLowerCase() : stringValue;
  const maxLength = MAX_FIELD_LENGTHS[field];

  return maxLength ? normalizedValue.slice(0, maxLength) : normalizedValue;
};

const normalizeStorybookDraftUserId = (value = '') => {
  if (value === undefined || value === null) {
    return '';
  }

  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim().slice(0, STORYBOOK_DRAFT_USER_ID_MAX_LENGTH)
    : '';
};

const createInitialFormData = (user, initialChildNeed = '') => ({
  // Child Information
  childName: '',
  childAge: '',
  childGender: '',
  childPersonality: '',
  favoriteActivities: '',

  // Story Preferences
  childNeed: normalizeOptionValue(supportNeeds, initialChildNeed),
  customNeedDetail: '',
  storyTheme: DEFAULT_STORY_WORLD_VALUE,
  customStoryWorldDetail: '',
  storyLength: 'medium',
  educationalFocus: '',
  moralLesson: '',

  // Character Customization
  mainCharacterType: 'child',
  characterTraits: '',
  sidekickCharacter: '',

  // Images
  childPhotos: [],
  inspirationImages: [],

  // Additional Details
  specialRequests: '',
  deliveryPreference: DELIVERY_AFTER_PREVIEW_VALUE,
  guardianConsent: false,

  // Contact
  parentName: user?.name || '',
  parentEmail: user?.email || '',
  phoneNumber: ''
});

const STORYBOOK_FORM_FIELD_KEYS = Object.keys(createInitialFormData(null));

export const sanitizeRecoveredStorybookFormData = (rawData = {}, baseData = createInitialFormData(null)) => {
  const recoveredData = rawData && typeof rawData === 'object' && !Array.isArray(rawData)
    ? rawData
    : {};
  const sanitizedData = { ...baseData };

  STORYBOOK_FORM_FIELD_KEYS.forEach((field) => {
    if (STORYBOOK_IMAGE_FIELDS.has(field)) {
      return;
    }

    if (STORYBOOK_BOOLEAN_FIELDS.has(field)) {
      sanitizedData[field] = hasAcceptedClientConsent(recoveredData[field]);
      return;
    }

    sanitizedData[field] = normalizeFormStringValue(field, recoveredData[field], sanitizedData[field] || '');
  });

  sanitizedData.deliveryPreference = normalizeDeliveryPreference(sanitizedData.deliveryPreference)
    || DELIVERY_AFTER_PREVIEW_VALUE;
  sanitizedData.childAge = normalizePrimitiveOptionValue(childAgeOptions, sanitizedData.childAge);
  sanitizedData.childGender = normalizeOptionValue(genderOptions, sanitizedData.childGender);
  sanitizedData.childNeed = normalizeOptionValue(supportNeeds, sanitizedData.childNeed);
  sanitizedData.storyTheme = normalizeOptionValue(storyWorlds, sanitizedData.storyTheme);
  sanitizedData.storyLength = normalizeOptionValue(storyLengthOptions, sanitizedData.storyLength, 'medium');
  sanitizedData.educationalFocus = normalizeOptionValue(educationalFoci, sanitizedData.educationalFocus);
  sanitizedData.mainCharacterType = normalizeOptionValue(mainCharacterOptions, sanitizedData.mainCharacterType, 'child');

  if (sanitizedData.childNeed !== CUSTOM_NEED_VALUE) {
    sanitizedData.customNeedDetail = '';
  }

  if (sanitizedData.storyTheme !== CUSTOM_STORY_WORLD_VALUE) {
    sanitizedData.customStoryWorldDetail = '';
  }

  sanitizedData.childPhotos = [];
  sanitizedData.inspirationImages = [];

  return sanitizedData;
};

export const applyInitialChildNeedToStorybookDraft = (draftData = {}, initialChildNeed = '') => {
  const normalizedInitialNeed = normalizeOptionValue(supportNeeds, initialChildNeed);

  if (!normalizedInitialNeed) {
    return draftData;
  }

  return {
    ...draftData,
    childNeed: normalizedInitialNeed,
    customNeedDetail: ''
  };
};

export const getSubmittableStorybookFormData = (rawData = {}) => {
  const normalizedData = sanitizeRecoveredStorybookFormData(rawData, createInitialFormData(null));

  return {
    ...normalizedData,
    guardianConsent: normalizedData.guardianConsent ? 'true' : '',
    childPhotos: Array.isArray(rawData.childPhotos) ? rawData.childPhotos : [],
    inspirationImages: Array.isArray(rawData.inspirationImages) ? rawData.inspirationImages : []
  };
};

export const getFirstIncompleteStorybookStep = (rawData = {}) => {
  const data = sanitizeRecoveredStorybookFormData(rawData, createInitialFormData(null));

  if (!data.childName.trim() || !data.childAge) {
    return 1;
  }

  if (
    !data.childNeed
    || (data.childNeed === CUSTOM_NEED_VALUE && !data.customNeedDetail.trim())
    || !data.storyTheme
    || (data.storyTheme === CUSTOM_STORY_WORLD_VALUE && !data.customStoryWorldDetail.trim())
    || !data.storyLength
  ) {
    return 2;
  }

  if (!data.mainCharacterType) {
    return 3;
  }

  if (
    !data.parentName.trim()
    || !EMAIL_PATTERN.test(data.parentEmail.trim())
    || !data.deliveryPreference
    || !data.guardianConsent
  ) {
    return 4;
  }

  return STEP_DETAILS.length;
};

export const getRecoverableDraftData = (formData) => {
  const {
    childPhotos,
    inspirationImages,
    parentName,
    parentEmail,
    phoneNumber,
    ...draftData
  } = sanitizeRecoveredStorybookFormData(
    formData,
    createInitialFormData(null)
  );

  return {
    ...draftData,
    guardianConsent: false
  };
};

export const hasRecoverableStorybookDraftContent = (formData = {}) => {
  const draftData = getRecoverableDraftData(formData);
  const meaningfulTextFields = [
    'childName',
    'childAge',
    'childGender',
    'childPersonality',
    'favoriteActivities',
    'childNeed',
    'customNeedDetail',
    'customStoryWorldDetail',
    'educationalFocus',
    'moralLesson',
    'characterTraits',
    'sidekickCharacter',
    'specialRequests'
  ];

  return meaningfulTextFields.some((field) => Boolean(String(draftData[field] || '').trim()))
    || (draftData.storyTheme && draftData.storyTheme !== DEFAULT_STORY_WORLD_VALUE)
    || draftData.storyLength !== 'medium'
    || draftData.mainCharacterType !== 'child'
    || draftData.deliveryPreference !== DELIVERY_AFTER_PREVIEW_VALUE;
};

export const hasUnrecoverableStorybookContactDetails = (
  formData = {},
  baseData = createInitialFormData(null)
) => {
  const currentName = normalizeFormStringValue('parentName', formData.parentName);
  const currentEmail = normalizeFormStringValue('parentEmail', formData.parentEmail);
  const currentPhone = normalizeFormStringValue('phoneNumber', formData.phoneNumber);
  const baseName = normalizeFormStringValue('parentName', baseData.parentName);
  const baseEmail = normalizeFormStringValue('parentEmail', baseData.parentEmail);

  return Boolean(
    currentPhone
    || (currentName && currentName !== baseName)
    || (currentEmail && currentEmail !== baseEmail)
  );
};

export const shouldRestoreStorybookDraftForUser = ({
  draftUserId = '',
  currentUserId = ''
} = {}) => {
  const normalizedCurrentUserId = normalizeStorybookDraftUserId(currentUserId);

  if (!normalizedCurrentUserId) {
    return false;
  }

  const normalizedDraftUserId = normalizeStorybookDraftUserId(draftUserId);

  return !normalizedDraftUserId || normalizedDraftUserId === normalizedCurrentUserId;
};

export const createStorybookDraftPayload = (formData, currentStep, savedAt = Date.now(), userId = '') => {
  const normalizedUserId = normalizeStorybookDraftUserId(userId);

  return {
    formData: getRecoverableDraftData(formData),
    currentStep: Math.min(Math.max(Number(currentStep) || 1, 1), STEP_DETAILS.length),
    savedAt,
    ...(normalizedUserId ? { userId: normalizedUserId } : {})
  };
};

export const getNeutralStorybookUploadFilename = (prefix, index, file = {}) => {
  const mimeType = String(file.type || '').toLowerCase();
  const extension = IMAGE_UPLOAD_EXTENSION_BY_MIME[mimeType] || 'jpg';

  return `${prefix}-${index + 1}.${extension}`;
};

export const getStorybookSubmitErrorState = (error, t) => {
  let errorMessage = t('storybookForm.errors.submitGeneric');
  const safeError = error || {};
  const responseCode = safeError.response?.data?.code;
  const authRecoveryRequired = Boolean(safeError.response?.status === 401 || responseCode === 'AUTH_REQUIRED');
  const errorCode = String(safeError.code || '');
  const rawErrorMessage = String(safeError.message || '').toLowerCase();
  const isTimeout = errorCode === 'ECONNABORTED' || rawErrorMessage.includes('timeout');
  const isNetworkFailure = !safeError.response && (
    errorCode === 'ERR_NETWORK'
    || rawErrorMessage === 'network error'
    || Boolean(safeError.request)
  );

  if (responseCode === 'FORM_FIELDS_TOO_LARGE') {
    errorMessage = t('storybookForm.errors.formFieldsTooLarge');
  } else if (safeError.response?.status === 413 || responseCode === 'UPLOAD_TOO_LARGE') {
    errorMessage = t('storybookForm.errors.filesTooLarge');
  } else if (responseCode === 'TOO_MANY_FILES') {
    errorMessage = t('storybookForm.errors.tooManyFiles');
  } else if (responseCode === 'DUPLICATE_FIELDS') {
    errorMessage = t('storybookForm.errors.duplicateFields');
  } else if (responseCode === 'UNEXPECTED_FIELDS') {
    errorMessage = t('storybookForm.errors.unexpectedFields');
  } else if (responseCode === 'UNEXPECTED_UPLOAD_FIELD') {
    errorMessage = t('storybookForm.errors.unexpectedUploadField');
  } else if (responseCode === 'INVALID_IMAGE_UPLOAD') {
    errorMessage = t('storybookForm.errors.invalidUpload');
  } else if (responseCode === 'IMAGE_STORAGE_FAILED') {
    errorMessage = t('storybookForm.errors.imageStorageFailed');
  } else if (responseCode === 'VALIDATION_ERROR') {
    errorMessage = t('storybookForm.errors.validationRejected');
  } else if (responseCode === 'FORBIDDEN_ORIGIN') {
    errorMessage = t('storybookForm.errors.forbiddenOrigin');
  } else if (responseCode === 'REQUEST_ALREADY_PROCESSING') {
    errorMessage = t('storybookForm.errors.requestAlreadyProcessing');
  } else if (safeError.response?.status === 429 || responseCode === 'RATE_LIMITED') {
    errorMessage = t('storybookForm.errors.rateLimited');
  } else if (authRecoveryRequired) {
    errorMessage = t('storybookForm.errors.sessionExpired');
  } else if (isTimeout) {
    errorMessage = t('storybookForm.errors.submitTimeout');
  } else if (isNetworkFailure) {
    errorMessage = t('storybookForm.errors.networkUnavailable');
  } else if (responseCode === 'AUTH_NOT_CONFIGURED') {
    errorMessage = t('storybookForm.errors.authNotConfigured');
  } else if (responseCode === 'STORYBOOK_ORDER_FAILED' || responseCode === 'METHOD_NOT_ALLOWED') {
    errorMessage = t('storybookForm.errors.submitGeneric');
  }

  return {
    authRecoveryRequired,
    errorMessage
  };
};

export const getStorybookSubmitSuccessState = (responseData = {}) => ({
  orderId: typeof responseData.orderId === 'string' ? responseData.orderId : '',
  wasDuplicateSubmission: Boolean(responseData.duplicate)
});

export const getStorybookSuccessDisplayData = ({
  currentFormData = {},
  submittedFormData = null
} = {}) => (
  submittedFormData && typeof submittedFormData === 'object' && !Array.isArray(submittedFormData)
    ? submittedFormData
    : currentFormData
);

export const getStorybookFileRejectionErrorKey = (errorCode = '') => {
  if (errorCode === 'file-too-large') {
    return 'rejectedTooLarge';
  }

  if (errorCode === 'too-many-files') {
    return 'tooManyFiles';
  }

  return 'rejectedFile';
};

const getStoredStorybookDraft = (storage) => {
  for (const key of STORYBOOK_DRAFT_KEYS) {
    const value = storage.getItem(key);

    if (value) {
      return value;
    }
  }

  return null;
};

const clearStoredStorybookDraft = (storage) => {
  STORYBOOK_DRAFT_KEYS.forEach((key) => storage.removeItem(key));
};

const saveStoredStorybookDraft = (storage, formData, currentStep, userId = '') => {
  if (!hasRecoverableStorybookDraftContent(formData)) {
    clearStoredStorybookDraft(storage);
    return;
  }

  storage.setItem(
    STORYBOOK_DRAFT_KEY,
    JSON.stringify(createStorybookDraftPayload(formData, currentStep, Date.now(), userId))
  );
};

const isRecoverableDraftFresh = (savedAt) => {
  if (!Number.isFinite(savedAt)) {
    return false;
  }

  const age = Date.now() - savedAt;
  return age >= 0 && age <= STORYBOOK_DRAFT_TTL_MS;
};

const formatFileSize = (size) => {
  if (!Number.isFinite(size) || size <= 0) {
    return '';
  }

  const sizeInMb = size / (1024 * 1024);

  if (sizeInMb >= 0.1) {
    return `${sizeInMb.toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

export const getStorybookCharacterCount = (value = '', maxLength = 0) => {
  const text = typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
  const max = Number(maxLength) || 0;
  const count = text.length;

  return {
    count,
    max,
    remaining: Math.max(max - count, 0),
    isNearLimit: max > 0 && count >= Math.ceil(max * 0.9)
  };
};

export const getStorybookFieldLengthState = (field = '', value = '', maxLengths = MAX_FIELD_LENGTHS) => {
  const maxLength = Number(maxLengths?.[field]) || 0;
  const characterCount = getStorybookCharacterCount(value, maxLength);

  return {
    ...characterCount,
    isTooLong: maxLength > 0 && characterCount.count > maxLength
  };
};

export const createStorybookClientRequestId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2, 12);
  return `request-${Date.now().toString(36)}-${randomPart}`;
};

export const shouldBlockStorybookNavigation = ({ isSubmitting = false, isProcessingImages = false } = {}) => (
  Boolean(isSubmitting || isProcessingImages)
);

export const shouldDisableStorybookSubmitButton = ({
  isSubmitting = false,
  isProcessingImages = false
} = {}) => (
  shouldBlockStorybookNavigation({ isSubmitting, isProcessingImages })
);

export const shouldStartStorybookSubmit = ({
  submitInFlight = false,
  isSubmitting = false,
  isProcessingImages = false
} = {}) => (
  !submitInFlight
  && !shouldBlockStorybookNavigation({ isSubmitting, isProcessingImages })
);

export const getStorybookDirectionPreviewState = (rawData = {}) => {
  const data = sanitizeRecoveredStorybookFormData(rawData, createInitialFormData(null));
  const hasCustomNeedDetail = data.childNeed !== CUSTOM_NEED_VALUE || Boolean(data.customNeedDetail.trim());
  const hasCustomStoryWorldDetail = data.storyTheme !== CUSTOM_STORY_WORLD_VALUE || Boolean(data.customStoryWorldDetail.trim());
  const items = [
    {
      key: 'need',
      value: data.childNeed,
      isComplete: Boolean(data.childNeed && hasCustomNeedDetail)
    },
    {
      key: 'world',
      value: data.storyTheme,
      isComplete: Boolean(data.storyTheme && hasCustomStoryWorldDetail)
    },
    {
      key: 'agePace',
      value: data.childAge && data.storyLength ? `${data.childAge}:${data.storyLength}` : '',
      isComplete: Boolean(data.childAge && data.storyLength)
    }
  ];

  return {
    isReady: items.every((item) => item.isComplete),
    items
  };
};

export const getStorybookUploadSizeBytes = (files = []) => (
  Array.isArray(files)
    ? files.reduce((total, file) => {
      const size = Number(file?.size);
      return total + (Number.isFinite(size) && size > 0 ? size : 0);
    }, 0)
    : 0
);

export const getStorybookCombinedUploadSizeState = ({
  existingFiles = [],
  nextFiles = [],
  maxBytes = MAX_TOTAL_IMAGE_UPLOAD_SIZE_BYTES
} = {}) => {
  const totalBytes = getStorybookUploadSizeBytes([
    ...asArray(existingFiles),
    ...asArray(nextFiles)
  ]);
  const normalizedMaxBytes = Number(maxBytes) || 0;

  return {
    totalBytes,
    maxBytes: normalizedMaxBytes,
    isTooLarge: normalizedMaxBytes > 0 && totalBytes > normalizedMaxBytes
  };
};

export const getStorybookUploadProgressPercent = ({ loaded = 0, total = 0 } = {}) => {
  const loadedBytes = Number(loaded);
  const totalBytes = Number(total);

  if (!Number.isFinite(loadedBytes) || !Number.isFinite(totalBytes) || totalBytes <= 0) {
    return null;
  }

  return Math.min(Math.max(Math.round((loadedBytes * 100) / totalBytes), 0), 100);
};

export const shouldCompressStorybookImage = (file = {}) => (
  String(file?.type || '').toLowerCase() !== 'image/gif'
);

export const shouldResetStorybookReviewConfirmation = ({
  reviewConfirmed = false,
  fieldName = ''
} = {}) => (
  Boolean(reviewConfirmed)
  && Boolean(fieldName)
  && fieldName !== 'reviewConfirmed'
);

export const getMissingStorybookFinalConfirmationField = ({
  guardianConsent = false,
  reviewConfirmed = false
} = {}) => {
  if (!guardianConsent) {
    return 'guardianConsent';
  }

  if (!reviewConfirmed) {
    return 'reviewConfirmed';
  }

  return '';
};

export const getStorybookFinalSubmitIssue = ({
  fieldErrors = [],
  guardianConsent = false,
  reviewConfirmed = false
} = {}) => {
  const firstFieldError = Array.isArray(fieldErrors)
    ? fieldErrors.find((error) => (
      error?.field && !STORYBOOK_FINAL_CONFIRMATION_FIELDS.has(error.field)
    ))
    : null;

  if (firstFieldError?.field) {
    return {
      type: 'field',
      ...firstFieldError
    };
  }

  const confirmationField = getMissingStorybookFinalConfirmationField({
    guardianConsent,
    reviewConfirmed
  });

  return confirmationField
    ? {
      type: 'confirmation',
      field: confirmationField
    }
    : null;
};

export const shouldWarnBeforeStorybookUnload = ({
  isSubmitting = false,
  isProcessingImages = false,
  hasUnsavedContactDetails = false,
  childPhotoCount = 0,
  inspirationImageCount = 0,
  submitSuccess = false
} = {}) => (
  !submitSuccess
  && (
    Boolean(isSubmitting)
    || Boolean(isProcessingImages)
    || Boolean(hasUnsavedContactDetails)
    || Number(childPhotoCount) > 0
    || Number(inspirationImageCount) > 0
  )
);

const UploadedImageItem = ({ displayName, file, onRemove, removeLabel }) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const fileSize = formatFileSize(file?.size);

  useEffect(() => {
    if (!file) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <li className="file-item uploaded-image-item">
      <span className="file-thumb" aria-hidden="true">
        {previewUrl ? (
          <img src={previewUrl} alt="" />
        ) : (
          <FaImage className="file-icon" />
        )}
      </span>
      <span className="file-details">
        <span className="file-name">{displayName}</span>
        {fileSize && <span className="file-meta">{fileSize}</span>}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="remove-btn"
        aria-label={removeLabel}
      >
        <FaTrash aria-hidden="true" />
      </button>
    </li>
  );
};

// File compression utility
const compressImage = (file, maxSizeMB = 1, outputName = 'storybook-image.jpg') => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Calculate new dimensions (max 1920px width/height)
      const maxDimension = 1920;
      let { width, height } = img;

      if (width > height && width > maxDimension) {
        height = (height * maxDimension) / width;
        width = maxDimension;
      } else if (height > maxDimension) {
        width = (width * maxDimension) / height;
        height = maxDimension;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels to get under size limit
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Unable to process image'));
            return;
          }

          const sizeMB = blob.size / (1024 * 1024);
          if (sizeMB <= maxSizeMB || quality <= 0.1) {
            // Create a new File object with the original name
            const compressedFile = new File([blob], outputName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            quality -= 0.1;
            tryCompress();
          }
        }, 'image/jpeg', quality);
      };

      tryCompress();
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read image'));
    };

    img.src = objectUrl;
  });
};

const StoryBookForm = ({ initialChildNeed = '', onAuthRequired }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const formRef = useRef(null);
  const stepHeadingRef = useRef(null);
  const submitErrorRef = useRef(null);
  const confirmationDialogRef = useRef(null);
  const clientRequestIdRef = useRef(createStorybookClientRequestId());
  const submitInFlightRef = useRef(false);
  const hasAttemptedDraftRestoreRef = useRef(false);
  const pendingFocusFieldRef = useRef('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState('');
  const [wasDuplicateSubmission, setWasDuplicateSubmission] = useState(false);
  const [submittedFormSnapshot, setSubmittedFormSnapshot] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [draftNoticeKey, setDraftNoticeKey] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [needsAuthRecovery, setNeedsAuthRecovery] = useState(false);

  const [formData, setFormData] = useState(() => createInitialFormData(user, initialChildNeed));
  const hasRequiredFinalConfirmations = Boolean(formData.guardianConsent && reviewConfirmed);
  const shouldBlockNavigation = shouldBlockStorybookNavigation({ isSubmitting, isProcessingImages });
  const shouldDisableSubmitButton = shouldDisableStorybookSubmitButton({ isSubmitting, isProcessingImages });
  const hasUnsavedContactDetails = hasUnrecoverableStorybookContactDetails(
    formData,
    createInitialFormData(user)
  );
  const shouldWarnOnUnload = shouldWarnBeforeStorybookUnload({
    isSubmitting,
    isProcessingImages,
    hasUnsavedContactDetails,
    childPhotoCount: formData.childPhotos.length,
    inspirationImageCount: formData.inspirationImages.length,
    submitSuccess
  });

  useEffect(() => {
    const normalizedInitialNeed = normalizeOptionValue(supportNeeds, initialChildNeed);

    if (!normalizedInitialNeed) {
      return;
    }

    setFormData(prev => (
      prev.childNeed
        ? prev
        : { ...prev, childNeed: normalizedInitialNeed }
    ));
  }, [initialChildNeed]);

  useEffect(() => {
    if (typeof window === 'undefined' || hasAttemptedDraftRestoreRef.current) {
      return;
    }

    hasAttemptedDraftRestoreRef.current = true;
    let shouldClearDraft = false;

    try {
      const savedDraft = getStoredStorybookDraft(window.sessionStorage);

      if (!savedDraft) {
        return;
      }

      shouldClearDraft = true;
      const parsedDraft = JSON.parse(savedDraft);
      const recoveredFormData = parsedDraft?.formData;
      const recoveredStep = Number(parsedDraft?.currentStep);
      const savedAt = Number(parsedDraft?.savedAt);

      if (!isRecoverableDraftFresh(savedAt)) {
        return;
      }

      if (!shouldRestoreStorybookDraftForUser({
        draftUserId: parsedDraft?.userId,
        currentUserId: user?.id
      })) {
        return;
      }

      let sanitizedDraftData = null;

      if (recoveredFormData && typeof recoveredFormData === 'object') {
        sanitizedDraftData = sanitizeRecoveredStorybookFormData(recoveredFormData, createInitialFormData(user));
        sanitizedDraftData = applyInitialChildNeedToStorybookDraft(sanitizedDraftData, initialChildNeed);
        sanitizedDraftData.guardianConsent = false;
        setFormData(sanitizedDraftData);
        setDraftNoticeKey('storybookForm.notice.draftRestored');
      }

      if (Number.isInteger(recoveredStep) && recoveredStep >= 1 && recoveredStep <= STEP_DETAILS.length) {
        const firstIncompleteStep = sanitizedDraftData
          ? getFirstIncompleteStorybookStep(sanitizedDraftData)
          : recoveredStep;
        setCurrentStep(Math.min(recoveredStep, firstIncompleteStep));
      }
    } catch (error) {
      console.warn('Could not restore storybook draft:', error);
    } finally {
      if (shouldClearDraft) {
        try {
          clearStoredStorybookDraft(window.sessionStorage);
        } catch (error) {
          console.warn('Could not clear storybook draft:', error);
        }
      }
    }
  }, [initialChildNeed, user]);

  useEffect(() => {
    if (typeof window === 'undefined' || submitSuccess) {
      return;
    }

    try {
      saveStoredStorybookDraft(window.sessionStorage, formData, currentStep, user?.id);
    } catch (error) {
      console.warn('Could not autosave storybook draft:', error);
    }
  }, [currentStep, formData, submitSuccess, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldWarnOnUnload) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldWarnOnUnload]);

  useEffect(() => {
    if (!submitError || typeof window === 'undefined') {
      return undefined;
    }

    const focusTimeout = window.setTimeout(() => {
      submitErrorRef.current?.focus?.({ preventScroll: true });
      submitErrorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);

    return () => {
      window.clearTimeout(focusTimeout);
    };
  }, [submitError]);

  const closeConfirmationDialog = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  useEffect(() => {
    if (!showConfirmation || typeof document === 'undefined') {
      return undefined;
    }

    const previouslyFocusedElement = document.activeElement;
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusTimeout = window.setTimeout(() => {
      confirmationDialogRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        closeConfirmationDialog();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = Array.from(
        confirmationDialogRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        confirmationDialogRef.current?.focus();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [closeConfirmationDialog, isSubmitting, showConfirmation]);

  const selectedDeliveryValue = normalizeDeliveryPreference(formData.deliveryPreference);
  const getDeliveryPreferenceLabel = (value) => {
    const deliveryOption = getDeliveryOption(value);
    return deliveryOption
      ? t(`storybookForm.options.delivery.${deliveryOption.value}.label`)
      : value;
  };
  const selectedDeliveryLabel = getDeliveryPreferenceLabel(formData.deliveryPreference);

  const getOptionLabel = (options, namespace, value, fallback = '') => {
    const option = options.find((item) => item.value === value);
    return option ? t(`storybookForm.options.${namespace}.${option.key}`) : (fallback || value);
  };

  const getSupportNeedLabel = (value) => getOptionLabel(supportNeeds, 'supportNeeds', value);
  const getStoryWorldLabel = (value) => getOptionLabel(storyWorlds, 'storyWorlds', value);
  const getEducationalFocusLabel = (value) => getOptionLabel(educationalFoci, 'educationalFoci', value);
  const getGenderLabel = (value) => getOptionLabel(genderOptions, 'gender', value);
  const getMainCharacterLabel = (value) => getOptionLabel(mainCharacterOptions, 'mainCharacter', value);
  const getStoryLengthLabel = (value) => getOptionLabel(storyLengthOptions, 'storyLength', value);
  const getDirectionPreviewItemText = (item) => {
    if (!item.isComplete) {
      return t(`storybookForm.directionPreview.items.${item.key}.pending`);
    }

    if (item.key === 'need') {
      return formData.childNeed === CUSTOM_NEED_VALUE
        ? formData.customNeedDetail
        : getSupportNeedLabel(formData.childNeed);
    }

    if (item.key === 'world') {
      return formData.storyTheme === CUSTOM_STORY_WORLD_VALUE
        ? formData.customStoryWorldDetail
        : getStoryWorldLabel(formData.storyTheme);
    }

    if (item.key === 'agePace') {
      return t('storybookForm.directionPreview.items.agePace.value', {
        age: formData.childAge,
        pace: getStoryLengthLabel(formData.storyLength)
      });
    }

    return '';
  };
  const getUploadedText = (count) => t('storybookForm.common.uploaded', { count });
  const getChildPhotoDisplayLabel = (index) => t('storybookForm.summary.childPhotoLabel', { number: index + 1 });
  const getInspirationImageDisplayLabel = (index) => t('storybookForm.summary.inspirationImageLabel', { number: index + 1 });
  const shouldShowFileSizeTips = submitError
    .toLowerCase()
    .includes(t('storybookForm.errors.fileTooLargeKeyword').toLowerCase());
  const isCustomNeedSelected = formData.childNeed === CUSTOM_NEED_VALUE;
  const isCustomStoryWorldSelected = formData.storyTheme === CUSTOM_STORY_WORLD_VALUE;
  const directionPreviewState = getStorybookDirectionPreviewState(formData);
  const uploadSafetyItems = asArray(t('storybookForm.upload.safetyItems', { returnObjects: true }));
  const shouldShowDraftStorageNotice = hasRecoverableStorybookDraftContent(formData);
  const requestLocale = String(i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('tr')
    ? 'tr'
    : 'en';

  const scrollFormIntoView = () => {
    window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const focusCurrentStepHeading = () => {
    window.requestAnimationFrame(() => {
      stepHeadingRef.current?.focus?.({ preventScroll: true });
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const clearSubmitFeedback = ({ includeAuthRecovery = false } = {}) => {
    if (includeAuthRecovery || !needsAuthRecovery) {
      setSubmitError('');
    }

    if (includeAuthRecovery) {
      setNeedsAuthRecovery(false);
    }
  };

  const showNonAuthSubmitError = (message) => {
    setNeedsAuthRecovery(false);
    setSubmitError(message);
  };

  const handleAuthRecovery = () => {
    if (typeof window !== 'undefined') {
      try {
        saveStoredStorybookDraft(window.sessionStorage, formData, currentStep, user?.id);
      } catch (error) {
        console.warn('Could not save storybook draft before auth recovery:', error);
      }
    }

    setNeedsAuthRecovery(false);
    setSubmitError('');

    if (typeof onAuthRequired === 'function') {
      onAuthRequired();
    }
  };

  // File size validation and compression
  const validateAndCompressFiles = async (
    files,
    type,
    getDisplayName = (index) => `${type} ${index + 1}`,
    getOutputName = (index) => `storybook-image-${index + 1}.jpg`
  ) => {
    const processedFiles = [];

    setIsProcessingImages(true);
    setUploadProgress(t('storybookForm.progress.processing', { count: files.length, type }));

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const displayName = getDisplayName(i, file);

        // Keep browser-side validation aligned with the server's allowed image types.
        if (!isAllowedStorybookImageFile(file)) {
          showNonAuthSubmitError(t('storybookForm.errors.invalidImage', { fileName: displayName }));
          return null;
        }

        if (Number.isFinite(file.size) && file.size <= 0) {
          showNonAuthSubmitError(t('storybookForm.errors.emptyImage', { fileName: displayName }));
          return null;
        }

        // Check original file size
        if (file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
          showNonAuthSubmitError(t('storybookForm.errors.fileTooLarge', { fileName: displayName }));
          return null;
        }

        if (!shouldCompressStorybookImage(file)) {
          processedFiles.push(file);
          continue;
        }

        try {
          setUploadProgress(t('storybookForm.progress.compressing', { fileName: displayName, current: i + 1, total: files.length }));
          const compressedFile = await compressImage(file, COMPRESSED_IMAGE_MAX_SIZE_MB, getOutputName(i, file));
          processedFiles.push(compressedFile);

        } catch (error) {
          console.error('Could not compress selected image:', {
            name: error.name,
            message: error.message
          });
          showNonAuthSubmitError(t('storybookForm.errors.imageProcessingFailed', { fileName: displayName }));
          return null;
        }
      }

      return processedFiles;
    } finally {
      setUploadProgress('');
      setIsProcessingImages(false);
    }
  };

  const handleInputChange = (e) => {
    const { checked, name, type, value } = e.target;
    const rawValue = type === 'checkbox' ? checked : value;
    const maxLength = MAX_FIELD_LENGTHS[name];
    const nextValue = typeof rawValue === 'string' && maxLength
      ? rawValue.slice(0, maxLength)
      : rawValue;

    clearSubmitFeedback();

    setFormData(prev => ({
      ...prev,
      [name]: nextValue,
      ...(name === 'childNeed' && nextValue !== CUSTOM_NEED_VALUE ? { customNeedDetail: '' } : {}),
      ...(name === 'storyTheme' && nextValue !== CUSTOM_STORY_WORLD_VALUE ? { customStoryWorldDetail: '' } : {})
    }));

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    if (shouldResetStorybookReviewConfirmation({ currentStep, reviewConfirmed, fieldName: name })) {
      setReviewConfirmed(false);
    }
  };

  const handleReviewConfirmationChange = (e) => {
    clearSubmitFeedback();
    setReviewConfirmed(e.target.checked);
    setTouchedFields(prev => ({
      ...prev,
      reviewConfirmed: true
    }));
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;

    if (typeof value !== 'string') {
      return;
    }

    const trimmedValue = value.trim();
    const normalizedValue = name === 'parentEmail' ? trimmedValue.toLowerCase() : trimmedValue;

    if (normalizedValue !== value) {
      clearSubmitFeedback();
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    setFormData(prev => ({
      ...prev,
      [name]: normalizedValue
    }));
  };

  const onDropChildPhotos = async (acceptedFiles) => {
    if (isProcessingImages) {
      showNonAuthSubmitError(t('storybookForm.errors.imagesProcessing'));
      return;
    }

    if (formData.childPhotos.length + acceptedFiles.length > CHILD_PHOTO_LIMIT) {
      showNonAuthSubmitError(t('storybookForm.errors.maxChildPhotos'));
      return;
    }

    clearSubmitFeedback();
    const processedFiles = await validateAndCompressFiles(
      acceptedFiles,
      t('storybookForm.upload.childPhotosType'),
      (index) => getChildPhotoDisplayLabel(formData.childPhotos.length + index),
      (index) => `child-photo-${formData.childPhotos.length + index + 1}.jpg`
    );

    if (processedFiles) {
      const uploadSizeState = getStorybookCombinedUploadSizeState({
        existingFiles: [
          ...formData.childPhotos,
          ...formData.inspirationImages
        ],
        nextFiles: processedFiles
      });

      if (uploadSizeState.isTooLarge) {
        showNonAuthSubmitError(t('storybookForm.errors.totalFileSize'));
        return;
      }

      setFormData(prev => ({
        ...prev,
        childPhotos: [...prev.childPhotos, ...processedFiles]
      }));
    }
  };

  const onDropInspirationImages = async (acceptedFiles) => {
    if (isProcessingImages) {
      showNonAuthSubmitError(t('storybookForm.errors.imagesProcessing'));
      return;
    }

    if (formData.inspirationImages.length + acceptedFiles.length > INSPIRATION_IMAGE_LIMIT) {
      showNonAuthSubmitError(t('storybookForm.errors.maxInspirationImages'));
      return;
    }

    clearSubmitFeedback();
    const processedFiles = await validateAndCompressFiles(
      acceptedFiles,
      t('storybookForm.upload.inspirationImagesType'),
      (index) => getInspirationImageDisplayLabel(formData.inspirationImages.length + index),
      (index) => `inspiration-image-${formData.inspirationImages.length + index + 1}.jpg`
    );

    if (processedFiles) {
      const uploadSizeState = getStorybookCombinedUploadSizeState({
        existingFiles: [
          ...formData.childPhotos,
          ...formData.inspirationImages
        ],
        nextFiles: processedFiles
      });

      if (uploadSizeState.isTooLarge) {
        showNonAuthSubmitError(t('storybookForm.errors.totalFileSize'));
        return;
      }

      setFormData(prev => ({
        ...prev,
        inspirationImages: [...prev.inspirationImages, ...processedFiles]
      }));
    }
  };

  const removeImage = (type, index) => {
    clearSubmitFeedback();

    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleRejectedFiles = (fileRejections, getDisplayName, options = {}) => {
    const firstRejection = fileRejections[0];

    if (!firstRejection) {
      return;
    }

    const firstError = firstRejection.errors?.[0];
    const fileName = getDisplayName(0);
    const errorKey = getStorybookFileRejectionErrorKey(firstError?.code);
    const message = errorKey === 'tooManyFiles'
      ? t(options.tooManyFilesKey || 'storybookForm.errors.tooManyFiles')
      : t(`storybookForm.errors.${errorKey}`, { fileName });

    showNonAuthSubmitError(message);
  };

  const { getRootProps: getChildPhotosProps, getInputProps: getChildPhotosInputProps } = useDropzone({
    onDrop: onDropChildPhotos,
    onDropRejected: (rejections) => handleRejectedFiles(
      rejections,
      (index) => getChildPhotoDisplayLabel(formData.childPhotos.length + index),
      { tooManyFilesKey: 'storybookForm.errors.maxChildPhotos' }
    ),
    accept: IMAGE_UPLOAD_ACCEPT,
    maxFiles: CHILD_PHOTO_LIMIT,
    maxSize: MAX_IMAGE_UPLOAD_SIZE_BYTES,
    disabled: shouldBlockNavigation
  });

  const { getRootProps: getInspirationProps, getInputProps: getInspirationInputProps } = useDropzone({
    onDrop: onDropInspirationImages,
    onDropRejected: (rejections) => handleRejectedFiles(
      rejections,
      (index) => getInspirationImageDisplayLabel(formData.inspirationImages.length + index),
      { tooManyFilesKey: 'storybookForm.errors.maxInspirationImages' }
    ),
    accept: IMAGE_UPLOAD_ACCEPT,
    maxFiles: INSPIRATION_IMAGE_LIMIT,
    maxSize: MAX_IMAGE_UPLOAD_SIZE_BYTES,
    disabled: shouldBlockNavigation
  });

  const getFieldError = (field, data = formData) => {
    const lengthState = getStorybookFieldLengthState(field, data[field]);

    if (lengthState.isTooLong) {
      return t('storybookForm.errors.fieldTooLong', { max: lengthState.max });
    }

    switch (field) {
      case 'childName':
        return data.childName.trim() ? '' : t('storybookForm.errors.childNameRequired');
      case 'childAge':
        if (!data.childAge) {
          return t('storybookForm.errors.childAgeRequired');
        }

        return hasPrimitiveValue(childAgeOptions, data.childAge) ? '' : t('storybookForm.errors.childAgeInvalid');
      case 'childGender':
        return data.childGender && !hasOptionValue(genderOptions, data.childGender)
          ? t('storybookForm.errors.childGenderInvalid')
          : '';
      case 'childNeed':
        if (!data.childNeed) {
          return t('storybookForm.errors.childNeedRequired');
        }

        return hasOptionValue(supportNeeds, data.childNeed) ? '' : t('storybookForm.errors.childNeedInvalid');
      case 'customNeedDetail':
        return data.childNeed === CUSTOM_NEED_VALUE && !data.customNeedDetail.trim()
          ? t('storybookForm.errors.customNeedDetailRequired')
          : '';
      case 'storyTheme':
        if (!data.storyTheme) {
          return t('storybookForm.errors.storyThemeRequired');
        }

        return hasOptionValue(storyWorlds, data.storyTheme) ? '' : t('storybookForm.errors.storyThemeInvalid');
      case 'customStoryWorldDetail':
        return data.storyTheme === CUSTOM_STORY_WORLD_VALUE && !data.customStoryWorldDetail.trim()
          ? t('storybookForm.errors.customStoryWorldDetailRequired')
          : '';
      case 'storyLength':
        return data.storyLength && hasOptionValue(storyLengthOptions, data.storyLength)
          ? ''
          : t('storybookForm.errors.storyLengthInvalid');
      case 'educationalFocus':
        return data.educationalFocus && !hasOptionValue(educationalFoci, data.educationalFocus)
          ? t('storybookForm.errors.educationalFocusInvalid')
          : '';
      case 'mainCharacterType':
        return data.mainCharacterType && hasOptionValue(mainCharacterOptions, data.mainCharacterType)
          ? ''
          : t('storybookForm.errors.mainCharacterTypeInvalid');
      case 'parentName':
        return data.parentName.trim() ? '' : t('storybookForm.errors.parentNameRequired');
      case 'parentEmail':
        if (!data.parentEmail.trim()) {
          return t('storybookForm.errors.parentEmailRequired');
        }

        return EMAIL_PATTERN.test(data.parentEmail.trim()) ? '' : t('storybookForm.errors.parentEmailInvalid');
      case 'deliveryPreference':
        if (!data.deliveryPreference) {
          return t('storybookForm.errors.deliveryRequired');
        }

        return isValidDeliveryPreference(data.deliveryPreference) ? '' : t('storybookForm.errors.deliveryPreferenceInvalid');
      case 'guardianConsent':
        return data.guardianConsent ? '' : t('storybookForm.errors.guardianConsentRequired');
      case 'reviewConfirmed':
        return reviewConfirmed ? '' : t('storybookForm.errors.reviewRequired');
      default:
        return '';
    }
  };

  const getStepFields = (step = currentStep, data = formData) => {
    switch (step) {
      case 1:
        return ['childName', 'childAge', 'childGender', 'childPersonality', 'favoriteActivities'];
      case 2:
        return [
          'childNeed',
          ...(data.childNeed === CUSTOM_NEED_VALUE ? ['customNeedDetail'] : []),
          'storyTheme',
          ...(data.storyTheme === CUSTOM_STORY_WORLD_VALUE ? ['customStoryWorldDetail'] : []),
          'storyLength',
          'educationalFocus',
          'moralLesson'
        ];
      case 3:
        return ['mainCharacterType', 'characterTraits', 'sidekickCharacter'];
      case 4:
        return ['parentName', 'parentEmail', 'phoneNumber', 'specialRequests', 'deliveryPreference', 'guardianConsent'];
      default:
        return [];
    }
  };

  const getStepErrorItems = (step = currentStep) => (
    getStepFields(step)
      .map((field) => ({
        field,
        step,
        message: getFieldError(field)
      }))
      .filter((item) => item.message)
  );

  const getAllStepErrorItems = () => (
    [1, 2, 3, 4].flatMap((step) => getStepErrorItems(step))
  );

  const markFieldsTouched = (fields) => {
    setTouchedFields(prev => fields.reduce((nextFields, field) => ({
      ...nextFields,
      [field]: true
    }), { ...prev }));
  };

  const focusField = useCallback((field) => {
    window.setTimeout(() => {
      const element = document.getElementById(field);
      element?.focus();
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, []);

  const focusFieldAfterStepRender = (field) => {
    pendingFocusFieldRef.current = field;
  };

  useEffect(() => {
    if (!pendingFocusFieldRef.current) {
      return;
    }

    const fieldToFocus = pendingFocusFieldRef.current;
    pendingFocusFieldRef.current = '';
    focusField(fieldToFocus);
  }, [currentStep, focusField]);

  const getRenderedFieldError = (field) => (
    touchedFields[field] ? getFieldError(field) : ''
  );

  const getInputErrorProps = (field, describedByIds = []) => {
    const error = getRenderedFieldError(field);
    const ariaDescribedBy = [
      ...describedByIds,
      error ? `${field}-error` : ''
    ].filter(Boolean).join(' ');

    return {
      ...(error ? { 'aria-invalid': 'true' } : {}),
      ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})
    };
  };

  const renderFieldError = (field) => {
    const error = getRenderedFieldError(field);

    return error ? (
      <p id={`${field}-error`} className="field-error" role="alert">
        {error}
      </p>
    ) : null;
  };

  const renderCharacterCounter = (field) => {
    const maxLength = MAX_FIELD_LENGTHS[field];

    if (!maxLength) {
      return null;
    }

    const characterCount = getStorybookCharacterCount(formData[field], maxLength);

    return (
      <p
        id={`${field}-counter`}
        className={`character-counter${characterCount.isNearLimit ? ' is-near-limit' : ''}`}
      >
        {t('storybookForm.common.characterCounter', {
          count: characterCount.count,
          max: characterCount.max
        })}
      </p>
    );
  };

  const validateStep = (step = currentStep) => {
    const errorItems = getStepErrorItems(step);
    return {
      isValid: errorItems.length === 0,
      errors: errorItems.map((item) => item.message),
      fields: errorItems.map((item) => item.field)
    };
  };

  const surfaceFinalSubmitIssue = (submitIssue, allErrors = []) => {
    if (submitIssue?.type === 'field') {
      markFieldsTouched(
        allErrors
          .filter((error) => !STORYBOOK_FINAL_CONFIRMATION_FIELDS.has(error.field))
          .map((error) => error.field)
      );
      showNonAuthSubmitError(submitIssue.message);

      if (submitIssue.step !== currentStep) {
        focusFieldAfterStepRender(submitIssue.field);
        setCurrentStep(submitIssue.step);
      } else {
        focusField(submitIssue.field);
      }

      return true;
    }

    if (submitIssue?.type === 'confirmation') {
      markFieldsTouched([submitIssue.field]);
      showNonAuthSubmitError(t('storybookForm.errors.reviewRequired'));
      focusField(submitIssue.field);
      return true;
    }

    return false;
  };

  const getFinalSubmitIssue = (allErrors = getAllStepErrorItems()) => (
    getStorybookFinalSubmitIssue({
      fieldErrors: allErrors,
      guardianConsent: formData.guardianConsent,
      reviewConfirmed
    })
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only submit if we're on the final step
    if (currentStep !== 4) {
      return;
    }

    if (isProcessingImages) {
      showNonAuthSubmitError(t('storybookForm.errors.imagesProcessing'));
      return;
    }

    const allErrors = getAllStepErrorItems();
    const submitIssue = getFinalSubmitIssue(allErrors);

    if (surfaceFinalSubmitIssue(submitIssue, allErrors)) {
      return;
    }

    // Show confirmation dialog instead of submitting immediately
    clearSubmitFeedback({ includeAuthRecovery: true });
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!shouldStartStorybookSubmit({
      submitInFlight: submitInFlightRef.current,
      isSubmitting,
      isProcessingImages
    })) {
      return;
    }

    const allErrors = getAllStepErrorItems();
    const submitIssue = getFinalSubmitIssue(allErrors);

    if (submitIssue) {
      setShowConfirmation(false);
      surfaceFinalSubmitIssue(submitIssue, allErrors);
      return;
    }

    submitInFlightRef.current = true;
    setShowConfirmation(false);
    setIsSubmitting(true);
    setSubmitError('');
    setWasDuplicateSubmission(false);
    setSubmittedFormSnapshot(null);
    setNeedsAuthRecovery(false);
    setUploadProgress(t('storybookForm.progress.preparing'));

    const submittableFormData = getSubmittableStorybookFormData(formData);

    // Calculate total file size
    const totalFileSize = getStorybookUploadSizeBytes([
      ...submittableFormData.childPhotos,
      ...submittableFormData.inspirationImages
    ]);
    const totalSizeMB = totalFileSize / (1024 * 1024);

    console.debug('Submitting storybook order:', {
      childPhotos: formData.childPhotos.length,
      inspirationImages: formData.inspirationImages.length,
      totalFileSize: `${totalSizeMB.toFixed(2)}MB`
    });

    // Check if total size is reasonable after browser-side compression.
    if (totalFileSize > MAX_TOTAL_IMAGE_UPLOAD_SIZE_BYTES) {
      showNonAuthSubmitError(t('storybookForm.errors.totalFileSize'));
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setUploadProgress('');
      return;
    }

    try {
      setUploadProgress(t('storybookForm.progress.creatingFormData'));
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(submittableFormData).forEach(key => {
        if (key !== 'childPhotos' && key !== 'inspirationImages') {
          formDataToSend.append(key, submittableFormData[key]);
        }
      });
      formDataToSend.append('requestLocale', requestLocale);
      formDataToSend.append('clientRequestId', clientRequestIdRef.current);

      // Add images without logging sensitive filenames.
      submittableFormData.childPhotos.forEach((file, index) => {
        formDataToSend.append(
          `childPhoto_${index}`,
          file,
          getNeutralStorybookUploadFilename('child-photo', index, file)
        );
      });

      submittableFormData.inspirationImages.forEach((file, index) => {
        formDataToSend.append(
          `inspirationImage_${index}`,
          file,
          getNeutralStorybookUploadFilename('inspiration-image', index, file)
        );
      });

      setUploadProgress(t('storybookForm.progress.uploadingToServer'));
      console.debug('Sending storybook order request to API...');

      const response = await axios.post('/api/submit-storybook-order', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            setUploadProgress(t('storybookForm.progress.uploadingToServer'));
            return;
          }

          const percentCompleted = getStorybookUploadProgressPercent(progressEvent);
          setUploadProgress(
            percentCompleted === null
              ? t('storybookForm.progress.uploadingToServer')
              : t('storybookForm.progress.uploadingPercent', { percent: percentCompleted })
          );
        }
      });

      if (response.data.success) {
        const successState = getStorybookSubmitSuccessState(response.data);
        console.debug('Storybook order submitted successfully.');
        if (typeof window !== 'undefined') {
          try {
            clearStoredStorybookDraft(window.sessionStorage);
          } catch (error) {
            console.warn('Could not clear storybook draft after submit:', error);
          }
        }
        setSubmittedOrderId(successState.orderId);
        setWasDuplicateSubmission(successState.wasDuplicateSubmission);
        setSubmittedFormSnapshot(submittableFormData);
        setSubmitSuccess(true);
      } else {
        throw new Error(response.data.error || t('storybookForm.errors.submissionFailed'));
      }
    } catch (error) {
      console.error('Storybook order submission failed:', {
        status: error.response?.status,
        code: error.response?.data?.code,
        name: error.name,
        message: error.message
      });

      const { authRecoveryRequired, errorMessage } = getStorybookSubmitErrorState(error, t);

      setNeedsAuthRecovery(authRecoveryRequired);
      setSubmitError(errorMessage);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleCancelSubmit = () => {
    closeConfirmationDialog();
  };

  const handleStartAnotherRequest = () => {
    if (typeof window !== 'undefined') {
      try {
        clearStoredStorybookDraft(window.sessionStorage);
      } catch (error) {
        console.warn('Could not clear storybook draft before starting another request:', error);
      }
    }

    clientRequestIdRef.current = createStorybookClientRequestId();
    submitInFlightRef.current = false;
    pendingFocusFieldRef.current = '';
    setFormData(createInitialFormData(user, initialChildNeed));
    setCurrentStep(1);
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setSubmittedOrderId('');
    setWasDuplicateSubmission(false);
    setSubmittedFormSnapshot(null);
    setSubmitError('');
    setDraftNoticeKey('');
    setUploadProgress('');
    setIsProcessingImages(false);
    setShowConfirmation(false);
    setReviewConfirmed(false);
    setTouchedFields({});
    setNeedsAuthRecovery(false);
    focusCurrentStepHeading();
  };

  const nextStep = () => {
    if (isProcessingImages) {
      showNonAuthSubmitError(t('storybookForm.errors.imagesProcessing'));
      return;
    }

    const validation = validateStep();

    if (!validation.isValid) {
      markFieldsTouched(validation.fields);
      showNonAuthSubmitError(validation.errors[0]);
      focusField(validation.fields[0]);
      return;
    }

    clearSubmitFeedback({ includeAuthRecovery: true });
    setCurrentStep(prev => Math.min(prev + 1, 4));
    focusCurrentStepHeading();
  };

  const prevStep = () => {
    if (isProcessingImages) {
      showNonAuthSubmitError(t('storybookForm.errors.imagesProcessing'));
      return;
    }

    clearSubmitFeedback({ includeAuthRecovery: true });
    // Reset review confirmation if going back from step 4
    if (currentStep === 4) {
      setReviewConfirmed(false);
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
    focusCurrentStepHeading();
  };

  const handleReviewEdit = (step, fieldToFocus) => {
    clearSubmitFeedback({ includeAuthRecovery: true });
    setReviewConfirmed(false);

    if (fieldToFocus && step !== currentStep) {
      focusFieldAfterStepRender(fieldToFocus);
    }

    setCurrentStep(step);

    if (fieldToFocus && step === currentStep) {
      focusField(fieldToFocus);
      return;
    }

    if (!fieldToFocus) {
      focusCurrentStepHeading();
      return;
    }

    scrollFormIntoView();
  };

  const successDisplayData = getStorybookSuccessDisplayData({
    currentFormData: formData,
    submittedFormData: submittedFormSnapshot
  });
  const confirmationDisplayData = getSubmittableStorybookFormData(formData);
  const successDeliveryLabel = getDeliveryPreferenceLabel(successDisplayData.deliveryPreference);
  const confirmationDeliveryLabel = getDeliveryPreferenceLabel(confirmationDisplayData.deliveryPreference);

  if (submitSuccess) {
    return (
      <div className="success-message">
        <div className="success-content">
          <h2>{t('storybookForm.success.title')}</h2>
          <p>{t('storybookForm.success.messagePrefix')} <strong>{successDisplayData.childName}</strong>{t('storybookForm.success.messageSuffix')}</p>
          {submittedOrderId && (
            <p className="success-reference">
              <strong>{t('storybookForm.success.referenceLabel')}</strong> {submittedOrderId}
            </p>
          )}
          {wasDuplicateSubmission && (
            <p className="success-duplicate-note" role="status">
              {t('storybookForm.success.duplicateNote')}
            </p>
          )}

          <div className="order-summary-success">
            <h3>{t('storybookForm.summary.orderSummary')}</h3>
            <div className="summary-details">
              <div className="summary-section">
                <h4>{t('storybookForm.summary.childInformation')}</h4>
                <p><strong>{t('storybookForm.summary.name')}:</strong> {successDisplayData.childName}</p>
                <p><strong>{t('storybookForm.summary.age')}:</strong> {t('storybookForm.common.yearsOld', { age: successDisplayData.childAge })}</p>
                {successDisplayData.childGender && <p><strong>{t('storybookForm.summary.gender')}:</strong> {getGenderLabel(successDisplayData.childGender)}</p>}
              </div>

              <div className="summary-section">
                <h4>{t('storybookForm.summary.storyDirection')}</h4>
                <p><strong>{t('storybookForm.summary.supportNeed')}:</strong> {getSupportNeedLabel(successDisplayData.childNeed)}</p>
                {successDisplayData.customNeedDetail && <p><strong>{t('storybookForm.summary.customNeedDetail')}:</strong> {successDisplayData.customNeedDetail}</p>}
                <p><strong>{t('storybookForm.summary.storyWorld')}:</strong> {getStoryWorldLabel(successDisplayData.storyTheme)}</p>
                {successDisplayData.customStoryWorldDetail && <p><strong>{t('storybookForm.summary.customStoryWorldDetail')}:</strong> {successDisplayData.customStoryWorldDetail}</p>}
                <p><strong>{t('storybookForm.summary.length')}:</strong> {getStoryLengthLabel(successDisplayData.storyLength)}</p>
                {successDisplayData.educationalFocus && <p><strong>{t('storybookForm.summary.educationalFocus')}:</strong> {getEducationalFocusLabel(successDisplayData.educationalFocus)}</p>}
              </div>

              <div className="summary-section">
                <h4>{t('storybookForm.summary.photosAndImages')}</h4>
                <p><strong>{t('storybookForm.summary.childPhotos')}:</strong> {getUploadedText(successDisplayData.childPhotos?.length || 0)}</p>
                <p><strong>{t('storybookForm.summary.inspirationImages')}:</strong> {getUploadedText(successDisplayData.inspirationImages?.length || 0)}</p>
              </div>

              <div className="summary-section">
                <h4>{t('storybookForm.summary.deliveryAndContact')}</h4>
                <p><strong>{t('storybookForm.summary.deliveryMethod')}:</strong> {successDeliveryLabel}</p>
                <p><strong>{t('storybookForm.summary.paymentStatus')}:</strong> {t('storybookForm.common.noPaymentCharged')}</p>
                <p><strong>{t('storybookForm.summary.contactEmail')}:</strong> {successDisplayData.parentEmail}</p>
              </div>
            </div>
          </div>

          <div className="next-steps">
            <h3>{t('storybookForm.success.nextTitle')}</h3>
            <div className="steps-grid">
              <div className="step-item">
                <span className="step-icon">01</span>
                <div>
                  <strong>{t('storybookForm.success.nextSteps.saved.title')}</strong>
                  <p>{t('storybookForm.success.nextSteps.saved.text')}</p>
                </div>
              </div>
              <div className="step-item">
                <span className="step-icon">02</span>
                <div>
                  <strong>{t('storybookForm.success.nextSteps.email.title')}</strong>
                  <p>{t('storybookForm.success.nextSteps.email.textPrefix')} <strong>{successDisplayData.parentEmail}</strong>.</p>
                </div>
              </div>
              <div className="step-item">
                <span className="step-icon">03</span>
                <div>
                  <strong>{t('storybookForm.success.nextSteps.review.title')}</strong>
                  <p>{t('storybookForm.success.nextSteps.review.text')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="success-actions">
            <button type="button" className="btn-secondary" onClick={handleStartAnotherRequest}>
              {t('storybookForm.success.startAnother')}
            </button>
            <Link to="/" className="btn-primary">
              {t('storybookForm.success.backHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="storybook-form-container">
      <div
        className="form-progress"
        role="progressbar"
        aria-label={t('storybookForm.progress.ariaLabel')}
        aria-valuemin="1"
        aria-valuemax={STEP_DETAILS.length}
        aria-valuenow={currentStep}
        aria-valuetext={t('storybookForm.common.stepOf', { current: currentStep, total: STEP_DETAILS.length })}
      >
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / STEP_DETAILS.length) * 100}%` }}
          ></div>
        </div>
        <div className="step-indicators">
          {STEP_DETAILS.map(step => (
            <div
              key={step.number}
              className={`step-indicator-item ${currentStep >= step.number ? 'active' : ''}`}
              aria-current={currentStep === step.number ? 'step' : undefined}
            >
              <span className="step-indicator">{step.number}</span>
              <span className="step-label">{t(`storybookForm.steps.${step.key}.label`)}</span>
            </div>
          ))}
        </div>
      </div>

      {draftNoticeKey && (
        <div className="form-info-message" role="status">
          {t(draftNoticeKey)}
        </div>
      )}

      {shouldShowDraftStorageNotice && !submitSuccess && (
        <p className="form-privacy-message">
          {t('storybookForm.notice.draftAutosave')}
        </p>
      )}

      {submitError && (
        <div ref={submitErrorRef} className="error-message" role="alert" tabIndex="-1">
          <strong>{t('storybookForm.common.errorPrefix')}:</strong> {submitError}
          {needsAuthRecovery && (
            <div className="error-help auth-recovery-help">
              <p>{t('storybookForm.errors.sessionExpiredHelp')}</p>
              <button type="button" className="error-action-btn" onClick={handleAuthRecovery}>
                {t('storybookForm.errors.signInAgainCta')}
              </button>
            </div>
          )}
          {shouldShowFileSizeTips && (
            <div className="error-help">
              <p><strong>{t('storybookForm.errors.fileSizeTipsTitle')}</strong></p>
              <ul>
                <li>{t('storybookForm.errors.fileSizeTips.jpeg')}</li>
                <li>{t('storybookForm.errors.fileSizeTips.resize')}</li>
                <li>{t('storybookForm.errors.fileSizeTips.fewer')}</li>
                <li>{t('storybookForm.errors.fileSizeTips.compress')}</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {uploadProgress && (
        <div className="upload-progress" role="status" aria-live="polite" aria-atomic="true">
          <div className="progress-info">
            <FaSpinner className="spinner" />
            <span>{uploadProgress}</span>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div
            className="confirmation-dialog"
            ref={confirmationDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="storybook-confirmation-title"
            aria-describedby="storybook-confirmation-description"
            tabIndex="-1"
          >
            <h2 id="storybook-confirmation-title">{t('storybookForm.confirmation.title')}</h2>
            <p id="storybook-confirmation-description">
              {t('storybookForm.confirmation.introPrefix')} <strong>{confirmationDisplayData.childName}</strong>.
            </p>

            <div className="confirmation-summary">
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.age')}:</strong> {t('storybookForm.common.yearsOld', { age: confirmationDisplayData.childAge })}
              </div>
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.supportNeed')}:</strong> {getSupportNeedLabel(confirmationDisplayData.childNeed)}
              </div>
              {confirmationDisplayData.customNeedDetail && (
                <div className="confirmation-item">
                  <strong>{t('storybookForm.summary.customNeedDetail')}:</strong> {confirmationDisplayData.customNeedDetail}
                </div>
              )}
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.storyWorld')}:</strong> {getStoryWorldLabel(confirmationDisplayData.storyTheme)}
              </div>
              {confirmationDisplayData.customStoryWorldDetail && (
                <div className="confirmation-item">
                  <strong>{t('storybookForm.summary.customStoryWorldDetail')}:</strong> {confirmationDisplayData.customStoryWorldDetail}
                </div>
              )}
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.length')}:</strong> {getStoryLengthLabel(confirmationDisplayData.storyLength)}
              </div>
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.contactEmail')}:</strong> {confirmationDisplayData.parentEmail}
              </div>
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.photos')}:</strong> {t('storybookForm.confirmation.photosSummary', {
                  childCount: confirmationDisplayData.childPhotos.length,
                  inspirationCount: confirmationDisplayData.inspirationImages.length
                })}
              </div>
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.delivery')}:</strong> {confirmationDeliveryLabel}
              </div>
              <div className="confirmation-item">
                <strong>{t('storybookForm.summary.payment')}:</strong> {t('storybookForm.common.noPaymentWillBeCharged')}
              </div>
            </div>

            <div className="confirmation-warning">
              <p><strong>{t('storybookForm.confirmation.important')}</strong></p>
              <ul>
                <li>{t('storybookForm.confirmation.warning.saved')}</li>
                <li>{t('storybookForm.confirmation.warning.storyDirection')}</li>
                <li>{t('storybookForm.confirmation.warning.email', { email: confirmationDisplayData.parentEmail })}</li>
                <li>{t('storybookForm.confirmation.warning.noPayment')}</li>
                <li>{t('storybookForm.confirmation.warning.followUp')}</li>
              </ul>
            </div>

            <div className="confirmation-actions">
              <button
                type="button"
                onClick={handleCancelSubmit}
                className="btn-secondary"
                disabled={shouldBlockNavigation}
              >
                {t('storybookForm.common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="btn-primary"
                disabled={shouldBlockNavigation}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="spinner" />
                    {uploadProgress ? uploadProgress : t('storybookForm.common.submitting')}
                  </>
                ) : (
                  t('storybookForm.common.submitRequest')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} className="storybook-form" noValidate>
        {currentStep === 1 && (
          <div className="form-step" aria-labelledby="storybook-step-child-title">
            <p className="form-step-kicker">{t('storybookForm.common.stepOf', { current: 1, total: 4 })}</p>
            <h2 id="storybook-step-child-title" ref={stepHeadingRef} tabIndex="-1">{t('storybookForm.steps.child.title')}</h2>
            <p className="form-guidance">{t('storybookForm.steps.child.guidance')}</p>

            <div className="form-group">
              <label htmlFor="childName">{t('storybookForm.form.childName')} *</label>
              <input
                id="childName"
                type="text"
                name="childName"
                value={formData.childName}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                required
                maxLength={MAX_FIELD_LENGTHS.childName}
                placeholder={t('storybookForm.form.childNamePlaceholder')}
                autoComplete="given-name"
                {...getInputErrorProps('childName')}
              />
              {renderFieldError('childName')}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="childAge">{t('storybookForm.form.childAge')} *</label>
                <select
                  id="childAge"
                  name="childAge"
                  value={formData.childAge}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  required
                  {...getInputErrorProps('childAge')}
                >
                  <option value="">{t('storybookForm.form.selectAge')}</option>
                  {childAgeOptions.map((age) => (
                    <option key={age} value={age}>{t('storybookForm.common.yearsOld', { age })}</option>
                  ))}
                </select>
                {renderFieldError('childAge')}
              </div>

              <div className="form-group">
                <label htmlFor="childGender">{t('storybookForm.form.gender')}</label>
                <select
                  id="childGender"
                  name="childGender"
                  value={formData.childGender}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  {...getInputErrorProps('childGender')}
                >
                  {genderOptions.map((option) => (
                    <option key={option.key} value={option.value}>
                      {t(`storybookForm.options.gender.${option.key}`)}
                    </option>
                  ))}
                </select>
                {renderFieldError('childGender')}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="childPersonality">{t('storybookForm.form.personality')}</label>
              <textarea
                id="childPersonality"
                name="childPersonality"
                value={formData.childPersonality}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                maxLength={MAX_FIELD_LENGTHS.childPersonality}
                placeholder={t('storybookForm.form.personalityPlaceholder')}
                rows="3"
                {...getInputErrorProps('childPersonality', ['childPersonality-counter'])}
              />
              {renderFieldError('childPersonality')}
              {renderCharacterCounter('childPersonality')}
            </div>

            <div className="form-group">
              <label htmlFor="favoriteActivities">{t('storybookForm.form.favoriteActivities')}</label>
              <textarea
                id="favoriteActivities"
                name="favoriteActivities"
                value={formData.favoriteActivities}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                maxLength={MAX_FIELD_LENGTHS.favoriteActivities}
                placeholder={t('storybookForm.form.favoriteActivitiesPlaceholder')}
                rows="3"
                {...getInputErrorProps('favoriteActivities', ['favoriteActivities-counter'])}
              />
              {renderFieldError('favoriteActivities')}
              {renderCharacterCounter('favoriteActivities')}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="form-step" aria-labelledby="storybook-step-story-title">
            <p className="form-step-kicker">{t('storybookForm.common.stepOf', { current: 2, total: 4 })}</p>
            <h2 id="storybook-step-story-title" ref={stepHeadingRef} tabIndex="-1">{t('storybookForm.steps.story.title')}</h2>
            <p className="form-guidance">{t('storybookForm.steps.story.guidance')}</p>

            <div className="form-group">
              <label htmlFor="childNeed">{t('storybookForm.form.supportNeed')} *</label>
              <select
                id="childNeed"
                name="childNeed"
                value={formData.childNeed}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                required
                {...getInputErrorProps('childNeed')}
              >
                <option value="">{t('storybookForm.form.chooseSupportNeed')}</option>
                {supportNeeds.map((need) => (
                  <option key={need.key} value={need.value}>{t(`storybookForm.options.supportNeeds.${need.key}`)}</option>
                ))}
              </select>
              {renderFieldError('childNeed')}
              <p className="form-help">{t('storybookForm.form.supportNeedHelp')}</p>
              <p className="form-safety-note">{t('storybookForm.form.supportNeedSafetyNote')}</p>
            </div>

            {isCustomNeedSelected && (
              <div className="form-group">
                <label htmlFor="customNeedDetail">{t('storybookForm.form.customNeedDetail')} *</label>
                <textarea
                  id="customNeedDetail"
                  name="customNeedDetail"
                  value={formData.customNeedDetail}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  required
                  maxLength={MAX_FIELD_LENGTHS.customNeedDetail}
                  placeholder={t('storybookForm.form.customNeedDetailPlaceholder')}
                  rows="3"
                  {...getInputErrorProps('customNeedDetail', ['customNeedDetail-help', 'customNeedDetail-counter'])}
                />
                {renderFieldError('customNeedDetail')}
                <p id="customNeedDetail-help" className="form-help">{t('storybookForm.form.customNeedDetailHelp')}</p>
                {renderCharacterCounter('customNeedDetail')}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="storyTheme">{t('storybookForm.form.storyWorld')} *</label>
              <select
                id="storyTheme"
                name="storyTheme"
                value={formData.storyTheme}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                required
                {...getInputErrorProps('storyTheme')}
              >
                <option value="">{t('storybookForm.form.chooseStoryWorld')}</option>
                {storyWorlds.map((theme) => (
                  <option key={theme.key} value={theme.value}>{t(`storybookForm.options.storyWorlds.${theme.key}`)}</option>
                ))}
              </select>
              {renderFieldError('storyTheme')}
              <p className="form-help">{t('storybookForm.form.storyWorldHelp')}</p>
            </div>

            {isCustomStoryWorldSelected && (
              <div className="form-group">
                <label htmlFor="customStoryWorldDetail">{t('storybookForm.form.customStoryWorldDetail')} *</label>
                <textarea
                  id="customStoryWorldDetail"
                  name="customStoryWorldDetail"
                  value={formData.customStoryWorldDetail}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  required
                  maxLength={MAX_FIELD_LENGTHS.customStoryWorldDetail}
                  placeholder={t('storybookForm.form.customStoryWorldDetailPlaceholder')}
                  rows="3"
                  {...getInputErrorProps('customStoryWorldDetail', ['customStoryWorldDetail-help', 'customStoryWorldDetail-counter'])}
                />
                {renderFieldError('customStoryWorldDetail')}
                <p id="customStoryWorldDetail-help" className="form-help">{t('storybookForm.form.customStoryWorldDetailHelp')}</p>
                {renderCharacterCounter('customStoryWorldDetail')}
              </div>
            )}

            <div className="form-group">
              <label>{t('storybookForm.form.storyLength')}</label>
              <div
                className="radio-group"
                role="radiogroup"
                aria-label={t('storybookForm.form.storyLength')}
                {...getInputErrorProps('storyLength')}
              >
                {storyLengthOptions.map((option) => (
                  <label key={option.key} className="radio-option">
                    <input
                      type="radio"
                      name="storyLength"
                      value={option.value}
                      checked={formData.storyLength === option.value}
                      onChange={handleInputChange}
                    />
                    {t(`storybookForm.options.storyLength.${option.key}`)}
                  </label>
                ))}
              </div>
              {renderFieldError('storyLength')}
            </div>

            <div className="story-direction-preview" aria-live="polite">
              <div className="story-direction-preview-copy">
                <p className="story-direction-preview-kicker">{t('storybookForm.directionPreview.eyebrow')}</p>
                <h3>{t('storybookForm.directionPreview.title')}</h3>
                <p>
                  {directionPreviewState.isReady
                    ? t('storybookForm.directionPreview.readyText')
                    : t('storybookForm.directionPreview.pendingText')}
                </p>
              </div>

              <div className="story-direction-preview-flow">
                {directionPreviewState.items.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 && <span className="story-direction-preview-arrow" aria-hidden="true">→</span>}
                    <div className={`story-direction-preview-step${item.isComplete ? ' is-complete' : ''}`}>
                      <span>{t(`storybookForm.directionPreview.items.${item.key}.label`)}</span>
                      <strong>{getDirectionPreviewItemText(item)}</strong>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="educationalFocus">{t('storybookForm.form.educationalFocus')}</label>
              <select
                id="educationalFocus"
                name="educationalFocus"
                value={formData.educationalFocus}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                {...getInputErrorProps('educationalFocus')}
              >
                <option value="">{t('storybookForm.form.chooseEducationalFocus')}</option>
                {educationalFoci.map((focus) => (
                  <option key={focus.key} value={focus.value}>{t(`storybookForm.options.educationalFoci.${focus.key}`)}</option>
                ))}
              </select>
              {renderFieldError('educationalFocus')}
            </div>

            <div className="form-group">
              <label htmlFor="moralLesson">{t('storybookForm.form.messageToReinforce')}</label>
              <textarea
                id="moralLesson"
                name="moralLesson"
                value={formData.moralLesson}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                maxLength={MAX_FIELD_LENGTHS.moralLesson}
                placeholder={t('storybookForm.form.messagePlaceholder')}
                rows="3"
                {...getInputErrorProps('moralLesson', ['moralLesson-counter'])}
              />
              {renderFieldError('moralLesson')}
              {renderCharacterCounter('moralLesson')}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="form-step" aria-labelledby="storybook-step-visuals-title">
            <p className="form-step-kicker">{t('storybookForm.common.stepOf', { current: 3, total: 4 })}</p>
            <h2 id="storybook-step-visuals-title" ref={stepHeadingRef} tabIndex="-1">{t('storybookForm.steps.visuals.title')}</h2>
            <p className="form-guidance">{t('storybookForm.steps.visuals.guidance')}</p>
            <p className="privacy-note">{t('storybookForm.steps.visuals.privacyNote')}</p>
            {uploadSafetyItems.length > 0 && (
              <ul
                id="upload-safety-note"
                className="upload-safety-note"
                aria-label={t('storybookForm.upload.safetyLabel')}
              >
                {uploadSafetyItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            <div className="form-group">
              <label id="childPhotos-label">{t('storybookForm.upload.childPhotos')}</label>
              <p id="childPhotos-help" className="help-text">{t('storybookForm.upload.childPhotosHelp')}</p>

              <div
                {...getChildPhotosProps({
                  className: 'dropzone',
                  'aria-labelledby': 'childPhotos-label',
                  'aria-describedby': 'childPhotos-help childPhotos-types upload-safety-note'
                })}
              >
                <input {...getChildPhotosInputProps({ 'aria-label': t('storybookForm.upload.childPhotosInputLabel') })} />
                <FaUpload className="upload-icon" />
                <p>{t('storybookForm.upload.childDropText')}</p>
                <p id="childPhotos-types" className="file-types">{t('storybookForm.upload.acceptedTypes')}</p>
              </div>

              <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {t('storybookForm.upload.childPhotosCount', { count: formData.childPhotos.length, max: CHILD_PHOTO_LIMIT })}
              </p>

              {formData.childPhotos.length > 0 && (
                <div className="uploaded-files">
                  <h4>{t('storybookForm.upload.childPhotosCount', { count: formData.childPhotos.length, max: CHILD_PHOTO_LIMIT })}</h4>
                  <ul className="uploaded-file-list">
                    {formData.childPhotos.map((file, index) => (
                      <UploadedImageItem
                        key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                        displayName={getChildPhotoDisplayLabel(index)}
                        file={file}
                        onRemove={() => removeImage('childPhotos', index)}
                        removeLabel={t('storybookForm.upload.removeFile', { fileName: getChildPhotoDisplayLabel(index) })}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="form-group">
              <label id="inspirationImages-label">{t('storybookForm.upload.inspirationImages')}</label>
              <p id="inspirationImages-help" className="help-text">{t('storybookForm.upload.inspirationImagesHelp')}</p>

              <div
                {...getInspirationProps({
                  className: 'dropzone',
                  'aria-labelledby': 'inspirationImages-label',
                  'aria-describedby': 'inspirationImages-help inspirationImages-types upload-safety-note'
                })}
              >
                <input {...getInspirationInputProps({ 'aria-label': t('storybookForm.upload.inspirationImagesInputLabel') })} />
                <FaUpload className="upload-icon" />
                <p>{t('storybookForm.upload.inspirationDropText')}</p>
                <p id="inspirationImages-types" className="file-types">{t('storybookForm.upload.acceptedTypes')}</p>
              </div>

              <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {t('storybookForm.upload.inspirationImagesCount', { count: formData.inspirationImages.length, max: INSPIRATION_IMAGE_LIMIT })}
              </p>

              {formData.inspirationImages.length > 0 && (
                <div className="uploaded-files">
                  <h4>{t('storybookForm.upload.inspirationImagesCount', { count: formData.inspirationImages.length, max: INSPIRATION_IMAGE_LIMIT })}</h4>
                  <ul className="uploaded-file-list">
                    {formData.inspirationImages.map((file, index) => (
                      <UploadedImageItem
                        key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                        displayName={getInspirationImageDisplayLabel(index)}
                        file={file}
                        onRemove={() => removeImage('inspirationImages', index)}
                        removeLabel={t('storybookForm.upload.removeFile', { fileName: getInspirationImageDisplayLabel(index) })}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>{t('storybookForm.form.characterCustomization')}</label>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="mainCharacterType">{t('storybookForm.form.mainCharacter')}</label>
                  <select
                    id="mainCharacterType"
                    name="mainCharacterType"
                    value={formData.mainCharacterType}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    {...getInputErrorProps('mainCharacterType')}
                  >
                    {mainCharacterOptions.map((option) => (
                      <option key={option.key} value={option.value}>
                        {t(`storybookForm.options.mainCharacter.${option.key}`)}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('mainCharacterType')}
                </div>
              </div>

              <label htmlFor="characterTraits" className="sr-only">{t('storybookForm.form.characterTraits')}</label>
              <textarea
                id="characterTraits"
                name="characterTraits"
                value={formData.characterTraits}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                maxLength={MAX_FIELD_LENGTHS.characterTraits}
                placeholder={t('storybookForm.form.characterTraitsPlaceholder')}
                rows="3"
                {...getInputErrorProps('characterTraits', ['characterTraits-counter'])}
              />
              {renderFieldError('characterTraits')}
              {renderCharacterCounter('characterTraits')}
            </div>

            <div className="form-group">
              <label htmlFor="sidekickCharacter">{t('storybookForm.form.sidekickCharacter')}</label>
              <input
                id="sidekickCharacter"
                type="text"
                name="sidekickCharacter"
                value={formData.sidekickCharacter}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                maxLength={MAX_FIELD_LENGTHS.sidekickCharacter}
                placeholder={t('storybookForm.form.sidekickPlaceholder')}
                {...getInputErrorProps('sidekickCharacter')}
              />
              {renderFieldError('sidekickCharacter')}
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="form-step" aria-labelledby="storybook-step-review-title">
            <p className="form-step-kicker">{t('storybookForm.common.stepOf', { current: 4, total: 4 })}</p>
            <h2 id="storybook-step-review-title" ref={stepHeadingRef} tabIndex="-1">{t('storybookForm.steps.review.title')}</h2>
            <p className="confirmation-intro">{t('storybookForm.steps.review.guidance')}</p>

            {/* Final Contact Information Fields */}
            <div className="final-contact-section">
              <h3>{t('storybookForm.form.confirmContact')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentName">{t('storybookForm.form.parentName')} *</label>
                  <input
                    id="parentName"
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    required
                    maxLength={MAX_FIELD_LENGTHS.parentName}
                    placeholder={t('storybookForm.form.parentNamePlaceholder')}
                    autoComplete="name"
                    {...getInputErrorProps('parentName')}
                  />
                  {renderFieldError('parentName')}
                </div>

                <div className="form-group">
                  <label htmlFor="parentEmail">{t('storybookForm.form.email')} *</label>
                  <input
                    id="parentEmail"
                    type="email"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    required
                    maxLength={MAX_FIELD_LENGTHS.parentEmail}
                    placeholder={t('storybookForm.form.emailPlaceholder')}
                    autoComplete="email"
                    {...getInputErrorProps('parentEmail')}
                  />
                  {renderFieldError('parentEmail')}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">{t('storybookForm.form.phone')}</label>
                <input
                  id="phoneNumber"
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  maxLength={MAX_FIELD_LENGTHS.phoneNumber}
                  placeholder={t('storybookForm.form.phonePlaceholder')}
                  autoComplete="tel"
                  {...getInputErrorProps('phoneNumber')}
                />
                {renderFieldError('phoneNumber')}
              </div>

              <div className="form-group">
                <label htmlFor="specialRequests">{t('storybookForm.form.specialRequests')}</label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  maxLength={MAX_FIELD_LENGTHS.specialRequests}
                  placeholder={t('storybookForm.form.specialRequestsPlaceholder')}
                  rows="4"
                  {...getInputErrorProps('specialRequests', ['specialRequests-counter'])}
                />
                {renderFieldError('specialRequests')}
                {renderCharacterCounter('specialRequests')}
              </div>

              <div className="form-group">
                <label>{t('storybookForm.form.deliveryPreference')}</label>
                <p className="help-text">{t('storybookForm.form.deliveryPreferenceHelp')}</p>
                <div
                  className="radio-group"
                  role="radiogroup"
                  aria-label={t('storybookForm.form.deliveryPreference')}
                  {...getInputErrorProps('deliveryPreference')}
                >
                  {DELIVERY_OPTIONS.map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="deliveryPreference"
                        value={option.value}
                        checked={selectedDeliveryValue === option.value}
                        onChange={handleInputChange}
                      />
                      <span className="delivery-option-copy">
                        <strong>{t(`storybookForm.options.delivery.${option.value}.label`)}</strong>
                        <small>{t(`storybookForm.options.delivery.${option.value}.description`)}</small>
                      </span>
                    </label>
                  ))}
                </div>
                {renderFieldError('deliveryPreference')}
                <p className="delivery-preference-note">{t('storybookForm.form.deliveryPreferenceNote')}</p>
              </div>
            </div>

            {/* Comprehensive Order Summary */}
            <h3 className="order-review-title">{t('storybookForm.summary.orderSummary')}</h3>
            <div className="order-review-sections">

              {/* Child Information Section */}
              <div className="review-section">
                <div className="review-section-header">
                  <h3>{t('storybookForm.summary.childInformation')}</h3>
                  <button
                    type="button"
                    className="review-edit-btn"
                    onClick={() => handleReviewEdit(1, 'childName')}
                    aria-label={t('storybookForm.summary.editSection', { section: t('storybookForm.summary.childInformation') })}
                  >
                    {t('storybookForm.common.edit')}
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.name')}:</strong> {formData.childName}
                  </div>
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.age')}:</strong> {t('storybookForm.common.yearsOld', { age: formData.childAge })}
                  </div>
                  {formData.childGender && (
                    <div className="review-item">
                      <strong>{t('storybookForm.summary.gender')}:</strong> {getGenderLabel(formData.childGender)}
                    </div>
                  )}
                  {formData.childPersonality && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.personality')}:</strong> {formData.childPersonality}
                    </div>
                  )}
                  {formData.favoriteActivities && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.favoriteActivities')}:</strong> {formData.favoriteActivities}
                    </div>
                  )}
                </div>
              </div>

              {/* Story Details Section */}
              <div className="review-section">
                <div className="review-section-header">
                  <h3>{t('storybookForm.summary.storyDirection')}</h3>
                  <button
                    type="button"
                    className="review-edit-btn"
                    onClick={() => handleReviewEdit(2, 'childNeed')}
                    aria-label={t('storybookForm.summary.editSection', { section: t('storybookForm.summary.storyDirection') })}
                  >
                    {t('storybookForm.common.edit')}
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.supportNeed')}:</strong> {getSupportNeedLabel(formData.childNeed)}
                  </div>
                  {formData.customNeedDetail && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.customNeedDetail')}:</strong> {formData.customNeedDetail}
                    </div>
                  )}
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.storyWorld')}:</strong> {getStoryWorldLabel(formData.storyTheme)}
                  </div>
                  {formData.customStoryWorldDetail && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.customStoryWorldDetail')}:</strong> {formData.customStoryWorldDetail}
                    </div>
                  )}
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.length')}:</strong> {getStoryLengthLabel(formData.storyLength)}
                  </div>
                  {formData.educationalFocus && (
                    <div className="review-item">
                      <strong>{t('storybookForm.summary.educationalFocus')}:</strong> {getEducationalFocusLabel(formData.educationalFocus)}
                    </div>
                  )}
                  {formData.moralLesson && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.messageToReinforce')}:</strong> {formData.moralLesson}
                    </div>
                  )}
                </div>
              </div>

              {/* Character Details Section */}
              <div className="review-section">
                <div className="review-section-header">
                  <h3>{t('storybookForm.summary.characterCustomization')}</h3>
                  <button
                    type="button"
                    className="review-edit-btn"
                    onClick={() => handleReviewEdit(3, 'mainCharacterType')}
                    aria-label={t('storybookForm.summary.editSection', { section: t('storybookForm.summary.characterCustomization') })}
                  >
                    {t('storybookForm.common.edit')}
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.mainCharacter')}:</strong> {getMainCharacterLabel(formData.mainCharacterType)}
                  </div>
                  {formData.sidekickCharacter && (
                    <div className="review-item">
                      <strong>{t('storybookForm.summary.sidekick')}:</strong> {formData.sidekickCharacter}
                    </div>
                  )}
                  {formData.characterTraits && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.characterTraits')}:</strong> {formData.characterTraits}
                    </div>
                  )}
                </div>
              </div>

              {/* Images Section */}
              <div className="review-section">
                <div className="review-section-header">
                  <h3>{t('storybookForm.summary.photosAndImages')}</h3>
                  <button
                    type="button"
                    className="review-edit-btn"
                    onClick={() => handleReviewEdit(3)}
                    aria-label={t('storybookForm.summary.editSection', { section: t('storybookForm.summary.photosAndImages') })}
                  >
                    {t('storybookForm.common.edit')}
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.childPhotos')}:</strong> {getUploadedText(formData.childPhotos.length)}
                    {formData.childPhotos.length > 0 && (
                      <div className="image-list">
                        {formData.childPhotos.map((file, index) => (
                          <span key={`${file.name}-${file.size}-${index}`} className="image-name">
                            • {t('storybookForm.summary.childPhotoLabel', { number: index + 1 })}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.inspirationImages')}:</strong> {getUploadedText(formData.inspirationImages.length)}
                    {formData.inspirationImages.length > 0 && (
                      <div className="image-list">
                        {formData.inspirationImages.map((file, index) => (
                          <span key={`${file.name}-${file.size}-${index}`} className="image-name">
                            • {t('storybookForm.summary.inspirationImageLabel', { number: index + 1 })}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact & Delivery Section */}
              <div className="review-section">
                <div className="review-section-header">
                  <h3>{t('storybookForm.summary.contactAndDelivery')}</h3>
                  <button
                    type="button"
                    className="review-edit-btn"
                    onClick={() => handleReviewEdit(4, 'parentName')}
                    aria-label={t('storybookForm.summary.editSection', { section: t('storybookForm.summary.contactAndDelivery') })}
                  >
                    {t('storybookForm.common.edit')}
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.parentName')}:</strong> {formData.parentName}
                  </div>
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.email')}:</strong> {formData.parentEmail}
                  </div>
                  {formData.phoneNumber && (
                    <div className="review-item">
                      <strong>{t('storybookForm.summary.phone')}:</strong> {formData.phoneNumber}
                    </div>
                  )}
                  <div className="review-item">
                    <strong>{t('storybookForm.summary.deliveryMethod')}:</strong> {selectedDeliveryLabel}
                  </div>
                  {formData.specialRequests && (
                    <div className="review-item full-width">
                      <strong>{t('storybookForm.summary.specialRequests')}:</strong> {formData.specialRequests}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Confirmation Notice */}
            <div className="confirmation-notice">
              <h3>{t('storybookForm.notice.title')}</h3>
              <p>{t('storybookForm.notice.intro')}</p>
              <ul>
                <li>{t('storybookForm.notice.items.correct')}</li>
                <li>{t('storybookForm.notice.items.photos')}</li>
                <li>{t('storybookForm.notice.items.guardian')}</li>
                <li>{t('storybookForm.notice.items.terms')}</li>
                <li>{t('storybookForm.notice.items.contact')}</li>
                <li>{t('storybookForm.notice.items.noPayment')}</li>
              </ul>
              <p><strong>{t('storybookForm.notice.followUp')}</strong></p>
            </div>

            {/* Review Confirmation Section */}
            <div className="review-confirmation-section">
              <div className="review-checkbox-container">
                <label className="review-checkbox-label">
                  <input
                    id="guardianConsent"
                    name="guardianConsent"
                    type="checkbox"
                    checked={formData.guardianConsent}
                    onChange={handleInputChange}
                    className="review-checkbox"
                    {...getInputErrorProps('guardianConsent')}
                  />
                  <span className="checkbox-checkmark"></span>
                  <div className="checkbox-text">
                    <strong>{t('storybookForm.guardianConsent.title')}</strong>
                    <p>{t('storybookForm.guardianConsent.text')}</p>
                  </div>
                </label>
                {renderFieldError('guardianConsent')}

                <label className="review-checkbox-label">
                  <input
                    id="reviewConfirmed"
                    name="reviewConfirmed"
                    type="checkbox"
                    checked={reviewConfirmed}
                    onChange={handleReviewConfirmationChange}
                    className="review-checkbox"
                    {...getInputErrorProps('reviewConfirmed')}
                  />
                  <span className="checkbox-checkmark"></span>
                  <div className="checkbox-text">
                    <strong>{t('storybookForm.reviewCheckbox.title')}</strong>
                    <p>{t('storybookForm.reviewCheckbox.text')}</p>
                  </div>
                </label>
                {renderFieldError('reviewConfirmed')}
                <p className="review-legal-note">
                  {t('storybookForm.reviewCheckbox.legalPrefix')}{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">
                    {t('storybookForm.reviewCheckbox.termsLink')}
                  </Link>
                  {' '}
                  {t('storybookForm.reviewCheckbox.legalMiddle')}{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                    {t('storybookForm.reviewCheckbox.privacyLink')}
                  </Link>
                  {t('storybookForm.reviewCheckbox.legalSuffix')}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn-secondary"
              disabled={shouldBlockNavigation}
            >
              {t('storybookForm.common.previous')}
            </button>
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn-primary"
              disabled={shouldBlockNavigation}
            >
              {isProcessingImages ? t('storybookForm.common.processingImages') : t('storybookForm.common.nextStep')}
            </button>
          ) : (
            <button
              type="submit"
              disabled={shouldDisableSubmitButton}
              className={`btn-primary submit-btn ${!hasRequiredFinalConfirmations ? 'needs-confirmation' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner" />
                  {uploadProgress ? uploadProgress : t('storybookForm.common.submittingOrder')}
                </>
              ) : isProcessingImages ? (
                t('storybookForm.common.processingImages')
              ) : !hasRequiredFinalConfirmations ? (
                t('storybookForm.common.reviewRequiredButton')
              ) : (
                t('storybookForm.common.proceedToConfirmation')
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default StoryBookForm;
